'use client'

import { usePathname } from 'next/navigation'
import type { Product } from 'product/types'
import type { ResponsiveProductCardSettings } from 'product-card/context/ProductCardConsumerLayoutContext/ProductCardConsumerLayoutContext'
import { ProductListContent } from 'product-list/components/ProductListContent/ProductListContent'
import { ProductListHook } from 'product-list/components/ProductListHook/ProductListHook'
import { ProductListFiltersProvider } from 'product-list/context/ProductListFiltersProvider/ProductListFiltersProvider'
import { ProductListProvider } from 'product-list/context/ProductListProvider/ProductListProvider'
import { usePageSize } from 'product-list/hooks/usePageSize/usePageSize'
import { useViewSearchParam } from 'product-list/hooks/useViewSearchParam/useViewSearchParam'
import type { SerializedFilters } from 'product-list/types/Filters'
import type {
	ProductListFilters,
	ProductListFiltersRef,
	ProductListGroup,
	ProductListRef,
	ProductListViewRef,
	RenderedProductListItem,
} from 'product-list/types/ProductsList'
import { View } from 'product-list/types/View'
import {
	forwardRef,
	type ReactNode,
	useImperativeHandle,
	useMemo,
	useRef,
} from 'react'

export type ProductListProductCardResponsiveSettings = Record<
	View,
	ResponsiveProductCardSettings
>

export interface ProductListProps {
	name: string
	groups: ProductListGroup[]
	filters?: ProductListFilters
	defaultView?: View
	displayViewSelector?: boolean
	preLoadedProductsData?: Product[]
	responsiveProductCardSettings?: ProductListProductCardResponsiveSettings
	serverProducts?: RenderedProductListItem[]
	inspirationalPresentation?: ReactNode
	shouldRenderSlideshowAnimation?: boolean
	additionalTopDistanceToSticky?: number
	onViewChange?: (view: View) => void
	onUserViewSelect?: (view: View) => void
	onUserApplyFilters?: (serializedFilters: SerializedFilters) => void
}

export const ProductList = forwardRef<
	ProductListRef,
	Readonly<ProductListProps>
>((params, ref) => {
	const {
		filters,
		displayViewSelector,
		groups,
		name,
		preLoadedProductsData,
		responsiveProductCardSettings,
		serverProducts,
		inspirationalPresentation,
		shouldRenderSlideshowAnimation,
		additionalTopDistanceToSticky,
		onViewChange,
		onUserViewSelect,
		onUserApplyFilters,
	} = params

	const currentView = useViewSearchParam()

	const defaultView = params.defaultView ?? View.Standard
	const initialView = currentView ?? defaultView

	const { filtersData, initialParams, resultPathname } = filters ?? {}

	const pathname = usePathname()

	const pageSize = usePageSize(initialView)

	const initialCount = useMemo(() => {
		if (serverProducts && serverProducts.length > 0) {
			return serverProducts.length
		}
		return Math.min(
			groups.reduce((acc, group) => acc + group.items.length, 0),
			pageSize
		)
	}, [pageSize, groups, serverProducts])

	const total = useMemo(
		() =>
			groups.reduce(
				(accumulator, group) => accumulator + group.items.length,
				0
			),
		[groups]
	)

	const viewRef = useRef<ProductListViewRef>(null)
	const filtersRef = useRef<ProductListFiltersRef>(null)

	useImperativeHandle(
		ref,
		() => ({
			setView: (view: View) => {
				viewRef.current?.setView(view)
			},
			clearFilters: () => {
				filtersRef.current?.clearFilters()
			},
		}),
		[]
	)

	return (
		<ProductListProvider
			ref={viewRef}
			onViewChange={onViewChange}
			initialView={initialView}
			defaultView={defaultView}
			initialCount={initialCount}
			name={name}
			total={total}
			shouldRenderSlideshowAnimation={!!shouldRenderSlideshowAnimation}
			hasInspirationalPresentation={!!inspirationalPresentation}
		>
			<ProductListFiltersProvider
				ref={filtersRef}
				filtersData={filtersData}
				initialParams={initialParams}
				resultPathname={resultPathname ?? pathname}
				onUserApplyFilters={onUserApplyFilters}
			>
				<ProductListHook
					additionalTopDistanceToSticky={additionalTopDistanceToSticky}
				/>
				<ProductListContent
					filters={filters}
					displayViewSelector={displayViewSelector}
					groups={groups}
					preLoadedProductsData={preLoadedProductsData}
					responsiveProductCardSettings={responsiveProductCardSettings}
					serverProducts={serverProducts}
					inspirationalPresentation={inspirationalPresentation}
					onUserViewSelect={onUserViewSelect}
					additionalTopDistanceToSticky={additionalTopDistanceToSticky}
				/>
			</ProductListFiltersProvider>
		</ProductListProvider>
	)
})
